import { extendTheme } from "native-base";

import { ButtonStyles } from "../components/controls/Button/ButtonStyles";
import CheckboxStyles from "../components/controls/Checkbox/CheckboxStyles";
import RadioStyles from "../components/controls/RadioList/RadioStyles";
import SwitchStyles from "../components/controls/Switch/SwitchStyles";
import { shadowOverrides } from "./shadows";

export const WhooshTheme = extendTheme({
  colors: {
    primary: {
      50: "white",
      100: "white",
      200: "#ACE5CF",
      300: "#A1D3C9",
      400: "#5CC1AD",
      500: "#00A887",
      600: "#188F62",
      700: "white",
      800: "#124734",
      900: "#124734",
      pressed: "#00755e",
    },
    secondary: {
      50: "white",
      100: "white",
      200: "white",
      300: "#FFDDD1",
      400: "#FF784B",
      500: "white",
      600: "white",
      700: "white",
      800: "#002726",
      900: "white",
    },
    neutrals: {
      white: "white",
      200: "#405D5C",
      300: "#809392",
      400: "#B8BFBF",
      500: "#FFECE5",
      600: "#dbdbdb",
      700: "#F0F0F0",
      800: "#F7F7F7",
      900: "#E5E5E5",
      950: "#002726",
      black: "black",
    },
    deepGreen: {
      60: "#67877B",
    },
    yellow: {
      400: "#F5DC5C",
      500: "#FFD600",
      600: "#EDE1A1",
    },
    orange: {
      500: "#FF784b",
    },
    danger: {
      400: "#F5857E",
      500: "#ff5959",
      600: "#EDB5B2",
    },
    grayscale: {
      800: "#F9F9F9",
      500: "#D2D9D9",
      300: "#B8BFBF",
    },
    events: {
      blue: "#5C8BCC",
      blueLight: "#DFE8F2",
      purple: "#8C81D6",
      purpleLight: "#EDEBFA",
      red: "#CC7A8F",
      redLight: "#FAE6EB",
      green: "#70CCA6",
      greenLight: "#E1F5ED",
      orange: "#E5A88A",
      orangeLight: "#FFECE5",
    },
    weather: {
      lightBlue: "#E5F4FF",
      blue: "#2196F3",
    },
  },
  fontConfig: {
    Times: {
      100: {
        normal: "Times-Regular",
      },
      200: {
        normal: "Times-Regular",
      },
      300: {
        normal: "Times-Regular",
      },
      400: {
        normal: "Times-Regular",
      },
      500: {
        normal: "Times-Regular",
      },
      600: {
        normal: "Times-Semibold",
      },
      700: {
        normal: "Times-Semibold",
      },
      800: {
        normal: "Times-Semibold",
      },
      900: {
        normal: "Times-Semibold",
      },
    },
    Inter: {
      100: {
        normal: "Inter_200ExtraLight",
      },
      200: {
        normal: "Inter_200ExtraLight",
      },
      300: {
        normal: "Inter_500Medium",
      },
      400: {
        normal: "Inter_400Regular",
      },
      500: {
        normal: "Inter_500Medium",
      },
      600: {
        normal: "Inter_600SemiBold",
      },
      700: {
        normal: "Inter_600SemiBold",
      },
      800: {
        normal: "Inter_600SemiBold",
      },
    },
  },
  fonts: {
    heading: "Times",
    body: "Inter",
    mono: "Inter",
  },
  shadows: {
    ...shadowOverrides,
  },
  components: {
    Button: {
      ...ButtonStyles,
    },
    Checkbox: {
      ...CheckboxStyles,
    },
    Radio: {
      ...RadioStyles,
    },
    Text: {
      baseStyle: {
        color: "black",
      },
    },
    Heading: {
      defaultProps: {
        letterSpacing: -0.2,
        lineHeight: "28px",
        fontVariant: ["lining-nums", "tabular-nums"],
        fontFamily: "heading",
      },
      baseStyle: {
        color: "neutrals.200",
      },
    },
    FormControl: {
      baseStyle: {
        marginBottom: 4,
      },
    },
    FormControlLabel: {
      baseStyle: {
        marginBottom: "0px",
        _text: {
          fontSize: "2xs",
          color: "neutrals.300",
        },
      },
    },
    Icon: {
      defaultProps: {
        size: "md",
      },
      sizes: {
        "2xs": 2,
        xs: 3,
        sm: 5,
        md: 6,
        lg: 7,
        xl: 8,
        "2xl": 9,
        "3xl": 10,
        "4xl": 11,
        "5xl": 12,
        "6xl": 16,
      },
    },
    Input: {
      baseStyle: (props: any) => {
        const styles: any = {
          borderRadius: "8",
        };
        if (props.isReadOnly) {
          styles.bg = "neutrals.800";
          styles.color = "neutrals.400";
          styles.borderWidth = 0;
        }
        return styles;
      },
      defaultProps: {
        size: "md",
        py: "18px",
        px: "5",
      },
      variants: {
        outline: (props: any) => {
          const styles: any = {
            borderWidth: "2",
          };
          if (props.isReadOnly) {
            styles.borderColor = "transparent";
          }
          if (props.onContentSizeChange) {
            styles._focus = {
              borderColor: "neutrals.black",
            };
          }
          return styles;
        },
      },
      sizes: {
        sm: { fontSize: 14 },
        md: { fontSize: 16 },
      },
    },
    InputGroup: {
      baseStyle: (props: any) => {
        const styles: any = {
          borderRadius: "8",
        };
        if (props.isReadOnly) {
          styles.bg = "neutrals.800";
          styles.color = "neutrals.400";
          styles.borderWidth = 0;
        }
        return styles;
      },
      defaultProps: {
        size: "md",
        py: "18px",
        px: "5",
      },
      variants: {
        outline: (props: any) => {
          const styles: any = {
            borderWidth: "2",
          };
          if (props.isReadOnly) {
            styles.borderColor = "transparent";
          }
          if (props.onContentSizeChange) {
            styles._focus = {
              borderColor: "neutrals.black",
            };
          }
          return styles;
        },
      },
      sizes: {
        md: { fontSize: 16 },
      },
    },
    Switch: {
      ...SwitchStyles,
    },
  },
});

export type CustomWhooshThemeType = typeof WhooshTheme;

declare module "native-base" {
  interface ICustomTheme extends CustomWhooshThemeType {}
}
